import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { Field, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Grid, FormControl, InputLabel, Select, TextField, makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import lineEndpoint from '../../../../api/services/lineEndpoint';
import { SERVICES_CODES, SERVICES_GROUPS, slicLineRequestConfig, stripLineRequestConfig } from 'app/config/services';
import { FormServiceContext } from 'app/utils/contexts';
import {
    clearSelectedLinesRequest,
    setSelectedEquipmentClassification,
    setSelectedEquipmentType,
    setSelectedLineId,
    setSelectedSubstituteEquipmentClassification,
    setSelectedSubstituteEquipmentType,
} from 'app/store/slices/line';
import helpers from 'app/utils/helpers';

import RegistrationStatus from '../../Registration/components/RegistrationStatus';
import PermissionariesTable from './PermissionariesTable';
import ChangeVehicle from './ChangeVehicle';
import LineDetails from './LineDetails';
import Successor from './Successor';
import SubstituteConductor from './SubstituteConductor';

import { StripLineConectionFields } from './StripLineConectionFields';
import { StripEquipmentSelector } from './StripEquipmentSelector';
import { StripTextField } from './StripTextField';
import { StripLineSelector } from './StripLineSelector';

import styles from '../styles';

const useStyles = makeStyles(styles);

function isValidResponse(response) {
    return response && response.status === 200 && response.data && response.data !== '';
}

const validateRequired = (value) => {
    let error;
    if (!value || value === '') {
        error = 'Campo obrigatório';
    }
    return error;
};

const validateCnpjSucessora = (value) => {
    let error = validateRequired(value);
    if (!helpers.validateCpfCnpj(value) || value.length < 18) {
        error = 'CNPJ inválido';
    }
    return error;
};

const RequestTabContent = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { errors, touched, loading, hasLine, companyData } = props;
    const { canEdit, isInternalForm } = useContext(FormServiceContext);

    const {
        allLines,
        permissionaries,
        selectedLineId,
        selectedLinesRequest,
        selectedEquipmentType,
        selectedEquipmentClassification,
        selectedSubstituteEquipmentType,
        selectedSubstituteEquipmentClassification,
    } = useSelector((state) => state.line);
    const service = useSelector((state) => state.services.details);
    const { vehicleType, vehicleClassification } = useSelector((state) => state.vehicle);

    const [lineExpired, setLineExpired] = useState(false);
    const [nmCompany, setNmCompany] = useState('');
    const [deCpfCnpj, setDeCpfCnpj] = useState('');
    const [selectedLine, setSelectedLine] = useState(null);
    const [loadingContract, setLoadingContract] = useState(false);
    const [selectedLineIds, setSelectedLineIds] = useState([]);

    useEffect(() => {
        if (!!companyData) {
            const doc = companyData.deCnpjMatriz || companyData.deCpf || companyData.deCpfCnpj;
            if (doc) {
                setDeCpfCnpj(helpers.formatarDocumento(doc));
            }

            const name = companyData.deRazaoSocial || companyData.nmCompleto || companyData.nmUsuario;
            if (name) {
                setNmCompany(name);
            }
        }
    }, [companyData]);

    useEffect(() => {
        if (allLines && selectedLineId) {
            const selectedLine = allLines.find((line) => line.cdLinha === selectedLineId);
            setSelectedLine(() => selectedLine);
        }
        // eslint-disable-next-line
    }, [allLines, selectedLineId]);

    const formikContext = useFormikContext();
    let isTransferService = SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA === service.cdServico;

    useEffect(() => {
        if (isTransferService && selectedLinesRequest.length !== 1) {
            formikContext.setValues({
                ...formikContext.values,
                nuTermoContrato: '',
                deTempoServico: '',
                nuApostila: '',
                dtVencimento: '',
                deTipoPrazo: '',
            });
        }
        // eslint-disable-next-line
    }, [selectedLinesRequest]);

    useEffect(() => {
        if (canEdit) {
            (async () => {
                if (isTransferService && (!selectedLinesRequest || selectedLinesRequest.length !== 1)) return;
                if (!selectedLineId) return;
                let cdLinha = isTransferService ? selectedLinesRequest[0].cdLinha : selectedLineId;
                formikContext.setValues({
                    ...formikContext.values,
                    nuTermoContrato: '',
                    deTempoServico: '',
                    nuApostila: '',
                    dtVencimento: '',
                    deTipoPrazo: '',
                });

                setLoadingContract(true);
                if (stripLineRequestConfig.withStripApostille.includes(service.cdServico)) {
                    let apostilleRes = await lineEndpoint.getLineLastApostille(cdLinha);
                    if (isValidResponse(apostilleRes)) {
                        const { nuApostila, nuApostilaFmt } = apostilleRes.data;
                        formikContext.setValues({ ...formikContext.values, nuApostila, nuApostilaFmt });
                    } else {
                        formikContext.setValues({ ...formikContext.values, nuApostila: '', nuApostilaFmt: '' });
                    }
                }

                let contractRes = await lineEndpoint.getLastContractsByLinha(cdLinha);
                if (isValidResponse(contractRes)) {
                    const { nuPrazo, dtVencimento, deTipoPrazo, nuTermo } = contractRes.data;
                    let expireDate = moment(dtVencimento);
                    let deTempoServico = nuPrazo + ' ' + deTipoPrazo;
                    formikContext.setValues({
                        ...formikContext.values,
                        nuTermoContrato: nuTermo,
                        dtVencimento: expireDate.format('DD/MM/YYYY'),
                        deTempoServico,
                    });
                    let isExpired = expireDate.isBefore(moment());
                    setLineExpired(isExpired);
                } else {
                    formikContext.setValues({
                        ...formikContext.values,
                        nuTermoContrato: '',
                        dtVencimento: '',
                        deTempoServico: '',
                    });
                }
                setLoadingContract(false);
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLinesRequest, selectedLineId]);

    const isStripContext = [...SERVICES_GROUPS.LINES_STRIP].includes(service.cdServico);
    const isStripLineConnection = service.cdServico === SERVICES_CODES.STRIP_CONEXAO_LINHAS;
    const isStripEquipmentSubst = service.cdServico === SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO;
    const isStripSectionImplantation = service.cdServico === SERVICES_CODES.STRIP_IMPLANTACAO_SECAO;
    const isStripSectionSuppression = service.cdServico === SERVICES_CODES.STRIP_SUPRESSAO_SECAO;
    const isStripServiceCanceling = service.cdServico === SERVICES_CODES.STRIP_CANCELAMENTO_SERVICO;
    const isStripScheduleModificationContext = stripLineRequestConfig.withStripSchedulesModification.includes(
        service.cdServico,
    );

    const handleChangeLinha = (e) => {
        dispatch(clearSelectedLinesRequest());
        dispatch(setSelectedLineId(Number(e.target.value)));
    };

    if (loading) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className={classes.centerProgress}>
                        <CircularProgress size={20} />
                    </div>
                </Grid>
            </Grid>
        );
    }

    if (!hasLine) {
        return <Alert severity="warning">Para a realização deste serviço, você deve possuir uma linha ativa.</Alert>;
    }

    const renderJustifyLabel = () => {
        return isStripContext ? 'Resumo da justificativa*' : 'Justificativa';
    };

    const renderStripSectionLabel = () => {
        return isStripSectionImplantation
            ? 'Seção(ões) a ser(em) implantada(s)*'
            : 'Seção(ões) a ser(em) suprimida(s)*';
    };

    return (
        <>
            <Grid container spacing={3} className={classes.containerMainData}>
                {isInternalForm && isStripContext && lineExpired && (
                    <Grid item xs={12}>
                        <Alert severity="warning">A Linha/Serviço "{selectedLine.deDescricao}" está vencida.</Alert>
                    </Grid>
                )}
                <RegistrationStatus {...props} canEdit={canEdit} classes={classes} />
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label={isStripContext ? 'Razão Social' : 'Nome / Razão Social'}
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={nmCompany}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label={isStripContext ? 'CNPJ' : 'CPF / CNPJ'}
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={deCpfCnpj}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>

                {stripLineRequestConfig.withStripLineListSelector.includes(service.cdServico) ? (
                    <Grid item xs={12}>
                        <StripLineSelector
                            loading={loadingContract}
                            selectedLineIds={selectedLineIds}
                            setSelectedLineIds={setSelectedLineIds}
                        />
                    </Grid>
                ) : (
                    <Grid item xs={12} sm={8}>
                        {!(isStripScheduleModificationContext || isStripServiceCanceling) && (
                            <FormControl variant="filled" margin="dense" fullWidth>
                                <InputLabel htmlFor="flLinha" shrink={true}>
                                    {isStripLineConnection ? 'Linha 1*' : 'Linha *'}
                                </InputLabel>
                                <Field
                                    name="linha"
                                    id="linha"
                                    onChange={handleChangeLinha}
                                    native
                                    disabled={!isStripContext || !canEdit}
                                    value={selectedLineId}
                                    as={Select}>
                                    {allLines ? (
                                        allLines.map((line, idx) => (
                                            <option key={`${idx}-${line.cdlinha}`} value={line.cdLinha}>
                                                {line.deDescricao}
                                            </option>
                                        ))
                                    ) : (
                                        <option>Nenhuma linha registrada</option>
                                    )}
                                </Field>
                            </FormControl>
                        )}

                        {(isStripScheduleModificationContext || isStripServiceCanceling) && (
                            <FormControl variant="filled" margin="dense" fullWidth>
                                <InputLabel htmlFor="flLinha" shrink={true}>
                                    {'Linha / Serviço'}
                                </InputLabel>
                                <Field
                                    name="linha"
                                    id="linha"
                                    onChange={handleChangeLinha}
                                    native
                                    disabled={!canEdit}
                                    value={selectedLineId}
                                    as={Select}>
                                    {allLines ? (
                                        allLines.map((line, idx) => (
                                            <option key={`${idx}-${line.cdlinha}`} value={line.cdLinha}>
                                                {line.deDescricao}
                                            </option>
                                        ))
                                    ) : (
                                        <option>Nenhuma linha / serviço registrado</option>
                                    )}
                                </Field>
                            </FormControl>
                        )}
                    </Grid>
                )}

                {stripLineRequestConfig.withStripApostille.includes(service.cdServico) && (
                    <Grid item xs={12} sm={4}>
                        <StripTextField
                            errors={errors}
                            touched={touched}
                            label="Nº da Apostila"
                            name="nuApostilaFmt"
                            disabled
                        />
                    </Grid>
                )}

                {stripLineRequestConfig.withStripServiceTime.includes(service.cdServico) && (
                    <Grid item xs={12} sm={4}>
                        <StripTextField
                            errors={errors}
                            touched={touched}
                            label="Tempo de serviço na linha"
                            name="deTempoServico"
                            disabled
                        />
                    </Grid>
                )}

                {isStripContext && (
                    <>
                        {isStripLineConnection && (
                            <StripLineConectionFields
                                handleChangeLinha={handleChangeLinha}
                                lines={allLines}
                                selectedLineId={selectedLineId}
                                disabled={!canEdit}
                                errors={errors}
                                touched={touched}
                            />
                        )}
                        {(isStripSectionImplantation || isStripSectionSuppression) && (
                            <Grid item xs={12} sm={8}>
                                <Field
                                    as={TextField}
                                    name={'deModificacaoSessao'}
                                    label={renderStripSectionLabel()}
                                    margin="dense"
                                    variant="filled"
                                    disabled={!canEdit}
                                    fullWidth
                                    validate={validateRequired}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={Boolean(errors.deModificacaoSessao)}
                                    helperText={
                                        Boolean(errors.deModificacaoSessao) && touched.deModificacaoSessao
                                            ? errors.deModificacaoSessao
                                            : ''
                                    }
                                    inputProps={{
                                        maxLength: 100,
                                    }}
                                />
                            </Grid>
                        )}

                        {stripLineRequestConfig.withStripDueDate.includes(service.cdServico) && (
                            <Grid item xs={12} sm={4}>
                                <StripTextField
                                    name="dtVencimento"
                                    errors={errors}
                                    disabled={true}
                                    touched={touched}
                                    label="Data de vencimento"
                                />
                            </Grid>
                        )}

                        {stripLineRequestConfig.withStripServiceField.includes(service.cdServico) && (
                            <Grid item xs={12} sm={8}>
                                <Field
                                    as={TextField}
                                    name={'deServicoSolicitado'}
                                    label={'Serviço solicitado*'}
                                    margin="dense"
                                    variant="filled"
                                    disabled={!canEdit}
                                    fullWidth
                                    validate={validateRequired}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={!!errors.deServicoSolicitado && touched.deServicoSolicitado}
                                    helperText={
                                        !!errors.deServicoSolicitado && touched.deServicoSolicitado
                                            ? errors.deServicoSolicitado
                                            : ''
                                    }
                                    inputProps={{
                                        maxLength: 100,
                                    }}
                                />
                            </Grid>
                        )}

                        {stripLineRequestConfig.withStripEquipmentSelector.includes(service.cdServico) && (
                            <Grid item xs={12} container spacing={2}>
                                <StripEquipmentSelector
                                    fieldTypeName="tipoVeiculo"
                                    fieldClassificationName="categoriaVeiculo"
                                    disabled={!canEdit || isStripEquipmentSubst}
                                    vehicleTypeList={vehicleType}
                                    vehicleClassificationList={vehicleClassification}
                                    selectedType={selectedEquipmentType}
                                    selectedCategory={selectedEquipmentClassification}
                                    onSelectType={(data) => dispatch(setSelectedEquipmentType(data))}
                                    onSelectClassification={(data) =>
                                        dispatch(setSelectedEquipmentClassification(data))
                                    }
                                />
                                {isStripEquipmentSubst && (
                                    <StripEquipmentSelector
                                        fieldTypeName="tipoVeiculoSubst"
                                        fieldClassificationName="categoriaVeiculoSubst"
                                        disabled={!canEdit}
                                        vehicleTypeList={vehicleType}
                                        vehicleClassificationList={vehicleClassification}
                                        selectedType={selectedSubstituteEquipmentType}
                                        selectedCategory={selectedSubstituteEquipmentClassification}
                                        onSelectType={(data) => dispatch(setSelectedSubstituteEquipmentType(data))}
                                        onSelectClassification={(data) =>
                                            dispatch(setSelectedSubstituteEquipmentClassification(data))
                                        }
                                    />
                                )}
                            </Grid>
                        )}

                        {stripLineRequestConfig.withStripLineListSelector.includes(service.cdServico) && (
                            <>
                                {SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA === service.cdServico ? (
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            as={TextField}
                                            name={'nuTermoContrato'}
                                            label={'Nº do Contrato de Concessão/Termo de permissão'}
                                            margin="dense"
                                            variant="filled"
                                            disabled
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} sm={4} />
                                )}

                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name={'deCnpjSucessora'}
                                        label={'CNPJ Sucessora*'}
                                        margin="dense"
                                        variant="filled"
                                        disabled={!canEdit}
                                        validate={validateCnpjSucessora}
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        value={formikContext.values['deCnpjSucessora'] || ''}
                                        onChange={(e) => {
                                            const cnpj = e.target.value;
                                            const formatted = helpers.formatarDocumento(cnpj);
                                            formikContext.setFieldValue('deCnpjSucessora', formatted);
                                        }}
                                        error={Boolean(errors.deCnpjSucessora)}
                                        helperText={Boolean(errors.deCnpjSucessora) ? errors.deCnpjSucessora : ''}
                                        inputProps={{
                                            maxLength: 18,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        as={TextField}
                                        name={'deRazaoSocialSucessora'}
                                        label={'Razão Social sucessora'}
                                        margin="dense"
                                        variant="filled"
                                        disabled={!canEdit}
                                        validate={validateRequired}
                                        required
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        error={Boolean(errors.deRazaoSocialSucessora)}
                                        helperText={
                                            Boolean(errors.deRazaoSocialSucessora) ? errors.deRazaoSocialSucessora : ''
                                        }
                                        inputProps={{
                                            maxLength: 100,
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        {stripLineRequestConfig.withStripPermissionAgreement.includes(service.cdServico) &&
                            !(SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA === service.cdServico) && (
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        as={TextField}
                                        name={'nuTermoContrato'}
                                        label={'Nº do Contrato de Concessão/Termo de permissão'}
                                        margin="dense"
                                        variant="filled"
                                        disabled
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            )}
                    </>
                )}

                {slicLineRequestConfig.withPermissionaries.includes(service.cdServico) && (
                    <PermissionariesTable
                        {...props}
                        data={!permissionaries ? [] : permissionaries.filter((p) => p.boAutorizado)}
                        loading={loading}
                        disabled={!canEdit}
                    />
                )}
                {slicLineRequestConfig.withVehicleChange.includes(service.cdServico) && (
                    <ChangeVehicle {...props} classes={classes} />
                )}
                {slicLineRequestConfig.withLineDetails.includes(service.cdServico) && (
                    <LineDetails {...props} cdServico={service.cdServico} classes={classes} />
                )}
                {slicLineRequestConfig.withSucessor.includes(service.cdServico) && (
                    <Successor {...props} classes={classes} />
                )}
                {slicLineRequestConfig.withSubstituteConductor.includes(service.cdServico) && (
                    <SubstituteConductor {...props} classes={classes} />
                )}
                <Grid item xs={12}>
                    <Field
                        name="deJustificativa"
                        label={renderJustifyLabel()}
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        error={!!errors.deJustificativa && touched.deJustificativa}
                        helperText={errors.deJustificativa && touched.deJustificativa ? errors.deJustificativa : ''}
                        multiline
                        minRows={4}
                        fullWidth
                        as={TextField}
                        inputProps={{
                            maxLength: 5000,
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
};
export default RequestTabContent;
